import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { catchError, from, mergeMap, Observable, throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';
import { CoreService } from '../services/core.service';
import { LoggingService } from '../services/Logging/logging.service';
import { MatDialog } from '@angular/material/dialog';
import { ResultModalComponent } from '../components/result-modal/result-modal.component';

export const ErrorInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const toast = inject(ToastService);
  const log = inject(LoggingService);
  const dialog = inject(MatDialog);

  const handleError = (error: any) => {
    console.error("Error interceptor: ", error);
    switch (error.status) {
      case 400:
        handleBadRequest(error);
        break;
      case 401:
        toast.showError(error.statusText == 'OK' ? "401 - Unauthorized" : error.statusText);
        break;
      case 404:
        router.navigateByUrl('/not-found').catch(err => `Failed to navigate: ${err}`);
        break;
      case 500:
        handleServerError(error);
        break;
      default:
        toast.showError(`Something unexpected went wrong. ${error.status} - ${error.statusText || ''}`);
        break;
    }
    return throwError(() => error);
  };

  const handleBadRequest = (error: any) => {
    if (error.error.errors) {
      const modalStateErrors = Object.values(error.error.errors).flat();
      throw modalStateErrors;
    } else if (typeof error.error === 'object') {
      openErrorDialog(error.error.message ?? error.error.Message);
    } else {
      openErrorDialog(error.error.message);
    }
  };

  const handleServerError = (error: any) => {
    const navigationExtras: NavigationExtras = {
      state: { error: error.error },
    };
    router.navigateByUrl('/server-error', navigationExtras).catch(err => `Failed to navigate: ${err}`);;
  };

  const openErrorDialog = (errorMessage: string) => {
    dialog.open(ResultModalComponent, {
      width: '35%',
      data: {
        isSuccess: false,
        headerText: 'Error',
        bodyText: errorMessage,
        buttonText: 'OK',
      },
    });
  };

  // Provide a way to skip this interceptor, add a skip-error-interceptor header
  // Is this good? Should we remove the header once this has been triggered?
  if (req.headers.has('skip-error-interceptor')) {
    return next(req);
  }

  return next(req).pipe(catchError((error: any) => {

    if (error.error instanceof Blob && error.error.type === 'application/json') {
      return from(error.error.text()).pipe(
        mergeMap((text: any) => {
          const parsedError = JSON.parse(text);

          //for some reason, the casing is all wonky after this parsing, so
          //I'm just gonna do this the dumb way for now, until I have
          //a better solution
          error.status = parsedError.Status;
          error.error.message = parsedError.Message;
          return handleError(error);
        }));
    } else {
      return handleError(error);

    }
  }));
};
